import { useCallback, useState } from 'react';
import { ApiError } from '#client/lib/api';

type MutationState =
  { status: 'idle' } | { status: 'pending' } | { status: 'error'; message: string };

type MutationOutcome<TResult> = { ok: true; value: TResult } | { ok: false; message: string };

interface Mutation {
  /**
   * Runs one request. Resolves to `{ ok: false, message }` rather than
   * rejecting, having also recorded the message as `errorMessage`, so a caller
   * only writes the success path — or toasts `message` instead of rendering it.
   */
  run: <TResult>(action: () => Promise<TResult>) => Promise<MutationOutcome<TResult>>;
  isPending: boolean;
  /** The failed request's message, until the next run or `reset`. */
  errorMessage: string | null;
  reset: () => void;
}

/**
 * One in-flight write and its outcome, for actions that are not a schema-backed
 * form (those use `useFormSubmit`): a status transition, a payment, a retry.
 *
 * The state is a single union, so "pending" and "failed" cannot both be true.
 * An `ApiError` carries the server's own message; anything else is a bug or a
 * dropped connection, and gets `fallbackMessage`.
 */
export function useMutation(fallbackMessage = 'That did not work. Please try again.'): Mutation {
  const [state, setState] = useState<MutationState>({ status: 'idle' });

  const run = useCallback(
    async <TResult>(action: () => Promise<TResult>): Promise<MutationOutcome<TResult>> => {
      setState({ status: 'pending' });
      try {
        const value = await action();
        setState({ status: 'idle' });
        return { ok: true, value };
      } catch (caught: unknown) {
        const message = caught instanceof ApiError ? caught.message : fallbackMessage;
        setState({ status: 'error', message });
        return { ok: false, message };
      }
    },
    [fallbackMessage],
  );

  const reset = useCallback(() => setState({ status: 'idle' }), []);

  return {
    run,
    isPending: state.status === 'pending',
    errorMessage: state.status === 'error' ? state.message : null,
    reset,
  };
}
